import React from 'react';
import styled from 'styled-components';

export type BeatStripProps = {
  title?: string;
  hint?: string;
  totalDurationMs?: number;
  trailing?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: 0 var(--space-2);
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
`;

const Title = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  letter-spacing: 0.02em;
`;

const Hint = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const Duration = styled.div`
  font-family: var(--font-mono);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const Scroller = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: var(--color-border-strong) transparent;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-xl);
  min-height: 232px;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border-strong);
    border-radius: var(--radius-pill);
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const Trailing = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
`;

const formatDuration = (ms: number): string => {
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  if (minutes === 0) return `${seconds.toFixed(1)}s`;
  return `${minutes}:${seconds.toFixed(0).padStart(2, '0')}`;
};

export const BeatStrip: React.FC<BeatStripProps> = ({
  title = 'Storyboard',
  hint,
  totalDurationMs,
  trailing,
  children,
  className,
}) => {
  return (
    <Root className={className}>
      <Header>
        <TitleBlock>
          <Title>{title}</Title>
          {hint && <Hint>{hint}</Hint>}
        </TitleBlock>
        {totalDurationMs !== undefined && <Duration>{formatDuration(totalDurationMs)}</Duration>}
      </Header>
      <Scroller>
        {children}
        {trailing && <Trailing>{trailing}</Trailing>}
      </Scroller>
    </Root>
  );
};

BeatStrip.displayName = 'BeatStrip';
